import { useMemo, useRef } from 'react'

import { neuerSchluessel } from '../lib/outbox'

/**
 * Where a post goes: trip, private chat (the other person), or the draft.
 *
 * One place for the string, so that the four callers of `useSendKey` cannot
 * name the same chat in two ways (1.75.2).
 */
export function sendeOrt(tripId: number, withUser: number | undefined, entwurf: boolean): string {
  return JSON.stringify([tripId, withUser ?? null, entwurf])
}

/**
 * One key per post, not per tap (1.75.0).
 *
 * From use: a message stood twice in the chat, two keys 2.8 seconds apart,
 * sent from a mobile network at an airport. The server had the first one;
 * its answer never arrived as an answer, the text stayed in the field, and
 * the second tap made a **new** key -- which the server had no reason to
 * refuse. The key was made per tap, and a tap is not a post.
 *
 * **The key stays while the content stays.** A second attempt with the same
 * text, pictures and reply carries the same key, and the server hands back
 * what it already has (`ClientKey`, `schonDa()`). Change the content and it
 * is a different post with a new key -- otherwise an edited retry would get
 * the old post back and the edit would vanish without a word.
 *
 * **And the place is part of the content** (1.75.2, `ort`). The composer is
 * not mounted anew when the chat changes -- `ThreadPage` stays, only its
 * route parameters move. Without the place, "ok" to Anna whose answer was
 * lost and then "ok" to Bernd carried one key: the server handed back Anna's
 * message, Bernd got nothing, and the sender saw Anna's message in Bernd's
 * chat until the next poll. Nothing reached the wrong person, but a post
 * vanished without a word.
 *
 * `forget()` after the post is really gone (the field emptied, the sheet
 * closed on success): the next post, even with the same words, is a new
 * one.
 *
 * A ref and not state: nothing is drawn from it, and it must be current in
 * the very tap that reads it (`CLAUDE.md`, "Ein Griff für eine Geste ist
 * ein `useRef`").
 */
export function useSendKey(ort: string): { keyFor: (content: string) => string; forget: () => void } {
  const held = useRef<{ content: string; key: string } | null>(null)
  return useMemo(
    () => ({
      keyFor(content: string): string {
        const was = JSON.stringify([ort, content])
        if (held.current === null || held.current.content !== was) {
          held.current = { content: was, key: neuerSchluessel() }
        }
        return held.current.key
      },
      forget(): void {
        held.current = null
      },
    }),
    [ort],
  )
}
